import get from 'lodash/get';
import has from 'lodash/has';
import every from 'lodash/every';
import noop from 'lodash/noop';
import toPairs from 'lodash/toPairs';
import fromPairs from 'lodash/fromPairs';
import map from 'lodash/map';
import union from 'lodash/union';
import { map as mapAsync } from 'p-iteration';
import { STATUS_ERROR } from 'box-ui-elements/es/constants';
import resin from '../../../common/utils/resin';
import {
  ACTION_ADD_ITEMS,
  ACTION_ITEMS_FILTERED_FROM_ADD,
  ACTION_UPLOAD_IN_PROGRESS,
  ACTION_SUBMIT_IN_PROGRESS,
  ACTION_SUBMIT,
  ACTION_SUBMISSION_FAILED,
  ACTION_REMOVE_ITEM,
  ACTION_TOGGLE_UPLOAD_MANAGER,
  ACTION_TOGGLE_TOS_CHECKBOX,
  ACTION_UPLOAD_PROGRESS_UPDATE,
  ACTION_ON_ALL_FILES_UPLOADED,
  ACTION_VALIDATION_FAILED,
  ACTION_CLOSE_NOTIFICATION,
  ACTION_SELECT_FIELD_ON_CHANGE,
  ACTION_TAXONOMY_FIELD_ON_CHANGE,
  ACTION_TEXT_FIELD_ON_CHANGE,
  ACTION_DATE_FIELD_ON_CHANGE,
  ACTION_TOO_MANY_FILES,
  ACTION_FILE_SIZE_LIMIT_EXCEEDED,
  ACTION_INVALID_FILE_NAME,
  ACTION_PROCESSING_IN_PROGRESS,
  ACTION_LIST_FIELD_ON_CHANGE,
  ACTION_NUMBER_FIELD_ON_CHANGE,
  ACTION_UPLOAD_FIELD_FILE_DESCRIPTION_ON_CHANGE,
  ACTION_SIMULATE_SUBMIT,
} from './form-submitter-constant';
import { formSubmitterSelectors } from './form-submitter-selector';
import FormResponseAPI from '../../../api/FormResponse';
import type {
  FormSubmitterState,
  FormSubmitterAction,
  FormSubmitterDispatch,
} from './form-submitter-type';
import { ItemQueue } from '../ItemQueue';
import { Uploader } from '../Uploader';
import {
  filterFilesNotAlreadyAdded,
  validateFormResponse,
  validateSelectField,
  validateTaxonomyField,
  validateTextField,
  validateDateField,
  validateUploadField,
  validateListField,
  validateMaximumSelections,
  validateNumberField,
  validateDescriptionForUploadField,
} from '../FormValidator';
import updateResponse from '../FormResponseAdapter';
import {
  ELEMENT_DESCRIPTION_ID,
  MAX_FILES_PER_SUBMISSION,
  FILES,
  MAX_FILE_SIZE,
  FORM_RESPONSE_STATUS,
  REGEX_FILE_NAME_VALIDATION,
  TYPE_CHECKBOX_FIELD,
  TYPE_SELECT_FIELD,
  FORM_TYPE_FORM,
} from '../../../constants';
import BoxFileQueue from '../../../common/utils/BoxFileQueue';
import { ERRORS } from '../FormErrors';

import type { SubmittedFormResponseFileField } from '../../../types/FormResponse';

export type ActionHandler = (
  action: FormSubmitterAction,
) => Promise<void> | void;

export type CreateActionsHandler = (
  dispatch: FormSubmitterDispatch,
  getState: () => FormSubmitterState,
) => ActionHandler;

type CreateActionsParams = {
  onInvalidFormError?: () => void;
  onSuccessfulSubmission?: (fileCounts?: number) => void;
};

export type CreateActionsResult = Record<string, CreateActionsHandler>;
export type CreateActions = (
  callbacks: CreateActionsParams,
) => CreateActionsResult;

type CreateActionsDefaultParams = Required<CreateActionsParams>;
const defaultCallbacks: CreateActionsDefaultParams = {
  onSuccessfulSubmission: noop,
  onInvalidFormError: noop,
} as const;

const resinEvent = {
  action: 'programmatic',
  page: 'file-request-submission',
  feature: 'filerequest',
  component: 'submissionpage',
} as const;

// @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
const allPatchOfFileIdCompleted = ({ formResponse }) =>
  every(formResponse, element => {
    if (get(element, 'type') !== FILES) return true;
    return every(get(element, 'data'), fileJSON => has(fileJSON, 'file_id'));
  });

const getOnUploadProgressUpdate =
  (
    dispatch: FormSubmitterDispatch,
    state: FormSubmitterState,
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    uploader: any,
    onComplete: (
      arg1:
        | {
            destroy: never;
            formResponseKey: never;
          }
        | {
            formResponseKey: string;
          },
    ) => void,
    boxFileQueue: BoxFileQueue,
  ) =>
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */ // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
  async (item: any, { formResponseKey, destroy }) => {
    // if 401 thrown, try fetching new token
    if (item.status === STATUS_ERROR && item.error?.status === 401) {
      uploader.onRetryErrorItem(item);
    }

    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2552
    const onSuccessfulPatch = (formResponse: FormResponse) => {
      if (allPatchOfFileIdCompleted(formResponse)) {
        onComplete({ formResponseKey, destroy });
      }
    };

    // If boxFile is available in item, that mean the item has been uploaded successfully to box.
    if (item.boxFile) {
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      const patchCall = (payload: any) =>
        FormResponseAPI.patchFileIds(
          formResponseKey,
          state.form.versionId,
          payload,
          state.isAuthenticationRequired,
        )
          .then(onSuccessfulPatch)
          .catch(e => {
            // eslint-disable-next-line
            console.error('patchFileIdToFormResponse: ', e);
            resin.recordAction({
              ...resinEvent,
              target: 'submit|error',
              data: {
                file_request_url_id: state.frUrlId,
                form_response_key: formResponseKey,
              },
            });
            dispatch({ type: ACTION_SUBMISSION_FAILED });
            destroy();
          });
      boxFileQueue.setPatchCall(patchCall);
      boxFileQueue.enqueue(item);
    }
    if (uploader.allUploadCompleted()) {
      dispatch({ type: ACTION_PROCESSING_IN_PROGRESS });
    }

    dispatch({ type: ACTION_UPLOAD_PROGRESS_UPDATE });
  };

const actionSubmit = async (
  dispatch: FormSubmitterDispatch,
  state: FormSubmitterState,
  { onSuccessfulSubmission, onInvalidFormError }: CreateActionsDefaultParams,
) => {
  const isForm = state.form.formType === FORM_TYPE_FORM;

  // Validate the form
  const errors = validateFormResponse(
    state.components,
    state.itemQueues,
    state.response,
    state.form.formType,
  );

  // If any errors exist, do NOT submit the form.
  if (Object.keys(errors).length > 0) {
    dispatch({ type: ACTION_VALIDATION_FAILED, errors });
    setTimeout(onInvalidFormError);
    return;
  }

  resin.recordAction({
    ...resinEvent,
    target: 'submit',
    data: {
      file_request_url_id: state.frUrlId,
      num_files_uploaded:
        formSubmitterSelectors.countAllItemsInItemQueues(state),
    },
  });

  // Set UI state to submit in progress
  dispatch({ type: ACTION_SUBMIT_IN_PROGRESS });

  const uploader = new Uploader(state.folderId, state.itemQueues);

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const fileDescriptionByElementId: Record<string, any> = {};

  let itemValues;
  try {
    // Get all upload items a format that form response likes
    itemValues = fromPairs(
      await mapAsync(
        toPairs(state.itemQueues),
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        async ([elementId, itemQueue]: [any, any]) => {
          const items = await mapAsync(itemQueue.items, async item => ({
            // @ts-expect-error [MIGRATION-SUPPRESSION]: TS18046
            local_path: item.file.name,
            size: get(item, 'size', null) || get(item, ['file', 'size'], null),
          }));

          // get a list of metadataFields which have a reference to `elementId`
          const metadataFieldIds =
            formSubmitterSelectors.getMetadataFieldsForUploadField(
              state,
              elementId,
            );
          const itemValue: SubmittedFormResponseFileField = {
            type: FILES,
            data: items,
            metadataFieldIds,
          };

          const folderId = formSubmitterSelectors.getFolderIdForUploadField(
            state,
            elementId,
          );
          const description = state.response[elementId]?.description;

          if (folderId) {
            itemValue.folderId = folderId;
          }

          if (description) {
            itemValue.description = description;
            fileDescriptionByElementId[elementId] = description;
          }

          // lodash fromPairs converts this tuple to a key: value pair in the reponse object
          const values = [elementId, itemValue];
          return values;
        },
      ),
    );
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  } catch (e: any) {
    dispatch({ type: ACTION_SUBMISSION_FAILED });
    // eslint-disable-next-line
    console.error('itemValues creation error: ', e);
    resin.recordAction({
      ...resinEvent,
      target: 'submit|sha_error',
      data: {
        file_request_url_id: state.frUrlId,
        error_code: get(e, 'code'),
        error_message: get(e, 'message'),
      },
    });
    return;
  }

  const items = union(...map(state.itemQueues, itemQueue => itemQueue.items));

  // Patch form response with status COMPLETE
  // If patch successful, go to form submit completed page
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
  const onComplete = ({ formResponseKey }) => {
    dispatch({ type: ACTION_ON_ALL_FILES_UPLOADED });
    // console.log('form submission completed...');
    resin.recordAction({
      ...resinEvent,
      target: 'submit|completed',
      data: {
        file_request_url_id: state.frUrlId,
        form_response_key: formResponseKey,
      },
    });
    onSuccessfulSubmission(items.length);
  };

  // This is queue is used to hold patch file id calls so that we can sychronize them in order
  // We need to do this until we can have locks on form response table update

  const boxFileQueue = new BoxFileQueue();

  // Options to be passed to uploader
  // isResumableUploadsEnabled
  // onUploadProgressUpdate - called as upload in progress (percentage update) and when upload is complete
  const options = {
    isResumableUploadsEnabled: state.isResumableUploadsEnabled,
    onUploadProgressUpdate: getOnUploadProgressUpdate(
      dispatch,
      state,
      uploader,
      onComplete,
      boxFileQueue,
    ),
    conflictCallback: (fileName: string) => {
      const extension = fileName.substr(fileName.lastIndexOf('.')) || '';
      const name = fileName.substr(0, fileName.lastIndexOf('.'));
      const now = new Date().toISOString();
      const nowFormatted = `${now.substr(0, now.lastIndexOf('.'))}`
        .replace(/:/g, '.')
        .replace('T', ' ');
      return `${name} ${nowFormatted}${extension}`;
    },
  } as const;

  // Initial submit of the form response
  let payload = {
    ...updateResponse(state.components, state.response),
    ...itemValues,
    ...(isForm
      ? {}
      : { folder: { type: 'folder', id: get(state, 'folderId', null) } }),
  };

  if (state.TosChecked) {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2353
    payload = { ...payload, acceptedTos: true };
  }

  const description = get(payload, ELEMENT_DESCRIPTION_ID);

  let formResponse;
  try {
    formResponse = await FormResponseAPI.createFormResponse(
      state.frUrlId,
      state.form.versionId,
      payload,
      state.isAuthenticationRequired,
    );
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  } catch (e: any) {
    // eslint-disable-next-line
    console.error('createFormResponse: ', e);
    resin.recordAction({
      ...resinEvent,
      target: 'submit|error',
      data: {
        file_request_url_id: state.frUrlId,
      },
    });
    dispatch({ type: ACTION_SUBMISSION_FAILED });
    return;
  }

  const fetchNewToken = () => {
    return FormResponseAPI.fetchToken(
      state.frUrlId,
      formResponse.key,
      state.isAuthenticationRequired,
    ).catch(e => {
      // eslint-disable-next-line
      console.error('fetchToken: ', e);
      resin.recordAction({
        ...resinEvent,
        target: 'submit|error',
        data: {
          file_request_url_id: state.frUrlId,
          form_response_key: formResponse.key,
        },
      });
      uploader.destroy();
      dispatch({ type: ACTION_SUBMISSION_FAILED });
      throw e;
    });
  };

  if (formResponse.status === FORM_RESPONSE_STATUS.COMPLETE) {
    onComplete({ formResponseKey: formResponse.key });
    return;
  }
  // set token and formReponse key received form initial submit of form response on uploader
  uploader.options = {
    ...uploader.options,
    ...options,
    // token is the uploadToken of the folder connected to a File Request
    token: formResponse.uploadToken,
    // tokenByFolderId is the uploadToken of each folder connected to an upload field in Forms
    tokenByFolderId: formResponse.uploadTokens,
    // description is the file description to be applied to all files in a File Request
    description,
    // fileDescriptionByElementId is the file description to be applied to files per upload field in Forms
    fileDescriptionByElementId,
  };
  uploader.fileRequestUrlId = state.frUrlId;
  uploader.formResponseKey = formResponse?.key;
  uploader.fetchNewToken = fetchNewToken;

  // Set UI state to upload in progress.
  dispatch({ type: ACTION_UPLOAD_IN_PROGRESS, uploader });

  // Begin upload.
  uploader.submit();
}; // end of actionSubmit

// For Form Preview, only validate the inputs and show the successful page. No Form will be submitted.
const actionSimulateSubmit = async (
  dispatch: FormSubmitterDispatch,
  state: FormSubmitterState,
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
  { onSuccessfulSubmission, onInvalidFormError },
) => {
  // Validate the form
  const errors = validateFormResponse(
    state.components,
    state.itemQueues,
    state.response,
    state.form.formType,
  );

  // If any errors exist, do NOT submit the form.
  if (Object.keys(errors).length > 0) {
    dispatch({ type: ACTION_VALIDATION_FAILED, errors });
    setTimeout(onInvalidFormError);
    return;
  }

  onSuccessfulSubmission();
};

const handleListFieldOnChange = (
  dispatch: FormSubmitterDispatch,
  state: FormSubmitterState,
  action: FormSubmitterAction,
  validateField: (
    required: boolean,
    values: Array<string>,
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2304
  ) => ErrorWithOverride | undefined,
) => {
  const { validateOnChange } = state;
  const { id, newValues } = action;
  const component = get(state, `components.${id}`);
  const { required, maximumSelections, type } = component;

  /* Continously validate maximum selections for checkbox and dropdown */
  if (type === TYPE_CHECKBOX_FIELD || type === TYPE_SELECT_FIELD) {
    const errorId = validateMaximumSelections(maximumSelections, newValues);
    if (errorId) {
      dispatch({ ...action, errorId });
      return;
    }
  }

  if (validateOnChange) {
    const errorId = validateField(required, newValues);
    dispatch({ ...action, errorId });
  } else {
    const errorId = get(state, `errors.${id}`);
    /* Don't allow maximum selections error to persist */
    if (errorId && errorId.id === ERRORS.MAXIMUM_SELECTIONS_EXCEEDED.id) {
      dispatch({ ...action, errorId: undefined });
    } else {
      dispatch({ ...action, errorId });
    }
  }
};

export const createActions: CreateActions = callbacks => {
  const { onSuccessfulSubmission, onInvalidFormError } = {
    ...defaultCallbacks,
    ...callbacks,
  } as const;
  return {
    [ACTION_ADD_ITEMS]: (dispatch, getState) => async action => {
      const { id, items } = action;
      const state = getState();

      if (
        items.length >
        MAX_FILES_PER_SUBMISSION -
          formSubmitterSelectors.countAllItemsInItemQueues(state)
      ) {
        dispatch({ type: ACTION_TOO_MANY_FILES });
        return;
      }

      const itemsArray = Array.from(items);

      const itemsToAdd = await filterFilesNotAlreadyAdded(
        itemsArray,
        formSubmitterSelectors.getAllItemsInItemQueues(state),
      );

      const hasFilesLargerThanMaxSize = Boolean(
        itemsToAdd.filter(file => file.size > MAX_FILE_SIZE).length,
      );

      if (hasFilesLargerThanMaxSize) {
        dispatch({ type: ACTION_FILE_SIZE_LIMIT_EXCEEDED });
        return;
      }

      const hasFilesInvalidName = itemsToAdd.find(file =>
        REGEX_FILE_NAME_VALIDATION.test(String(file.name)),
      );

      if (hasFilesInvalidName !== undefined) {
        dispatch({ type: ACTION_INVALID_FILE_NAME });
        return;
      }

      if (itemsToAdd.length !== itemsArray.length) {
        dispatch({
          type: ACTION_ITEMS_FILTERED_FROM_ADD,
        });
        if (itemsToAdd.length === 0) return;
      }

      // By default, use the folderId in the upload field. If none, use the file request's folder id
      const uploadFieldFolderId = get(
        state,
        `components.${id}.folderId`,
        state.folderId,
      );

      const itemQueue = get(
        state,
        `itemQueues.${id}`,
        new ItemQueue(id, uploadFieldFolderId),
      );
      itemQueue.addFilesToUploadQueue(itemsToAdd);

      const { validateOnChange } = state;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateUploadField(required, itemQueue.items);
        dispatch({ ...action, items: itemsToAdd, itemQueue, errorId });
      } else {
        dispatch({
          ...action,
          items: itemsToAdd,
          itemQueue,
          errorId: get(state, `errors.${id}`),
        });
      }
    },
    [ACTION_REMOVE_ITEM]: (dispatch, getState) => action => {
      const state = getState();
      const { id, item } = action;
      // By default, use the folderId in the upload field. If none, use the file request's folder id
      const uploadFieldFolderId = get(
        state,
        `components.${id}.folderId`,
        state.folderId,
      );
      const itemQueue = get(
        state,
        `itemQueues.${id}`,
        new ItemQueue(id, uploadFieldFolderId),
      );
      itemQueue.removeItem(item);
      const { validateOnChange } = state;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateUploadField(required, itemQueue.items);
        dispatch({ ...action, itemQueue, errorId });
      } else {
        dispatch({
          ...action,
          itemQueue,
          errorId: get(state, `errors.${id}`),
        });
      }
    },
    [ACTION_TAXONOMY_FIELD_ON_CHANGE]: (dispatch, getState) => action => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValues } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateTaxonomyField(required, newValues);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_SELECT_FIELD_ON_CHANGE]: (dispatch, getState) => action => {
      const state = getState();
      handleListFieldOnChange(dispatch, state, action, validateSelectField);
    },
    [ACTION_LIST_FIELD_ON_CHANGE]: (dispatch, getState) => action => {
      const state = getState();
      handleListFieldOnChange(dispatch, state, action, validateListField);
    },
    [ACTION_TEXT_FIELD_ON_CHANGE]: (dispatch, getState) => action => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValue } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { textType, required } = component;
        const errorId = validateTextField(textType, required, newValue);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_DATE_FIELD_ON_CHANGE]: (dispatch, getState) => action => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValue } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateDateField(required, newValue);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_NUMBER_FIELD_ON_CHANGE]: (dispatch, getState) => action => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValue } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateNumberField(required, newValue);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_UPLOAD_FIELD_FILE_DESCRIPTION_ON_CHANGE]:
      (dispatch, getState) => action => {
        const state = getState();
        const { validateOnChange } = state;
        const { id, newValue } = action;
        if (validateOnChange) {
          const component = get(state, `components.${id}`);
          const { showFileDescription, required } = component;
          const errorId = validateDescriptionForUploadField(
            showFileDescription,
            required,
            newValue,
          );
          dispatch({ ...action, errorId });
        } else {
          dispatch({ ...action, errorId: get(state, `errors.${id}`) });
        }
      },
    [ACTION_TOGGLE_UPLOAD_MANAGER]: dispatch => action => {
      dispatch(action);
    },
    [ACTION_TOGGLE_TOS_CHECKBOX]: dispatch => action => {
      dispatch(action);
    },
    [ACTION_CLOSE_NOTIFICATION]: dispatch => action => {
      dispatch(action);
    },
    [ACTION_SUBMIT]: (dispatch, getState) => async () => {
      const state = getState();
      await actionSubmit(dispatch, state, {
        onSuccessfulSubmission,
        onInvalidFormError,
      });
    },
    [ACTION_SIMULATE_SUBMIT]: (dispatch, getState) => async () => {
      const state = getState();
      await actionSimulateSubmit(dispatch, state, {
        onSuccessfulSubmission,
        onInvalidFormError,
      });
    },
  };
};
